import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"

import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

// ACTIONS
const loggedIn = createAction("auth/loggedIn")
export const loggedOut = createAction("auth/loggedOut")
const signedUp = createAction("auth/signedUp")
const loggedInByOAuth = createAction("auth/loggedInByOAuth")
export const oAuthInfoConfirmed = createAction("auth/oAuthInfoConfirmed")

const assumedIn = createAction("auth/assumedIn")
const assumedOut = createAction("auth/assumedOut")

const requested = createAction("auth/requested")
const failed = createAction("auth/failed")

export const setTempToken = createAction("auth/setTempToken")

const requestedVerifyOTP = createAction("auth/requestedVerifyOTP")
const receivedVerifyOTP = createAction("auth/receivedVerifyOTP")
const failedVerifyOTP = createAction("auth/failedVerifyOTP")

const requestedResendOtp = createAction("auth/requestedResendOtp")
const receivedResendOtp = createAction("auth/receivedResendOtp")
const failedResendOtp = createAction("auth/failedResendOtp")

const getInitialState = () => {
  const accessToken = localStorage.getItem("assume_access_token") || localStorage.getItem("access_token")
  const persistedTempToken = sessionStorage.getItem("oauth_temp_token");
  return {
    ...LOADING_INITIAL_STATE,
    loggedIn: !!accessToken,
    user: {},
    temp_token: persistedTempToken || null,
    resendOtp: {
      ...LOADING_INITIAL_STATE
    },
    verifyByOTP: {
      ...LOADING_INITIAL_STATE
    }
  }
}

export const getAuthInitialState = getInitialState

const initialState = getInitialState()

// REDUCER

const authReducer = createReducer(initialState, {
  [requested.type]: (state) => {
    setRequested(state)
  },
  [loggedIn.type]: (state, { payload }) => {
    setReceived(state)
    state.loggedIn = true
    localStorage.setItem("access_token", payload.access_token)
    localStorage.setItem("refresh_token", payload.refresh_token)
  },
  [loggedInByOAuth.type]: (state, { payload }) => {
    setReceived(state)
    if (payload?.access_token && payload?.refresh_token) {
      localStorage.setItem("access_token", payload.access_token)
      localStorage.setItem("refresh_token", payload.refresh_token)
    }
    if (payload?.temp_token) {
      state.temp_token = payload?.temp_token
      sessionStorage.setItem("oauth_temp_token", payload?.temp_token)
    }
    if (!payload?.user?.oauth_in_progress) {
      state.loggedIn = true
    }
  },
  [oAuthInfoConfirmed.type]: (state) => {
    state.loggedIn = true
  },
  [loggedOut.type]: (state) => {
    setReceived(state)
    state.loggedIn = false
    localStorage.removeItem("access_token")
    localStorage.removeItem("refresh_token")
    localStorage.removeItem("guest_token")
  },
  [signedUp.type]: (state,{ payload }) => {
    state.temp_token = payload?.encrypted_uuid
    state.user = payload?.user
    sessionStorage.setItem("oauth_temp_token", payload?.encrypted_uuid)
    setReceived(state)
  },
  
  [assumedIn.type]: (state, { payload }) => {
    setReceived(state)
    state.loggedIn = true
    localStorage.setItem("assume_access_token", payload.access_token)
    localStorage.setItem("assume_refresh_token", payload.refresh_token)
    localStorage.setItem("assume_redirect_url", payload.redirect_url)

    window.location.replace(`${window.location.origin}/#/`)
  },
  [assumedOut.type]: (state) => {
    setReceived(state)
    state.loggedIn = !!localStorage.getItem("access_token")
  },
  [failed.type]: (state) => {
    setFailed(state)
  },
  [setTempToken.type]: (state, { payload }) => {
    state.temp_token = payload
    sessionStorage.setItem("oauth_temp_token", payload)
  },
  [requestedVerifyOTP.type]: (state) => {
    setRequested(state.verifyByOTP)
  },
  [receivedVerifyOTP.type]: (state, { payload }) => {
    if (payload?.access_token && payload?.refresh_token) {
      localStorage.setItem("access_token", payload.access_token)
      localStorage.setItem("refresh_token", payload.refresh_token)
      state.temp_token = null
      sessionStorage.removeItem("oauth_temp_token")
    }
    if(payload?.user){
        state.loggedIn = !payload.user?.oauth_in_progress
    }
    setReceived(state.verifyByOTP)
  },
  [failedVerifyOTP.type]: (state) => {
    setFailed(state.verifyByOTP)
  },
  [requestedResendOtp.type]: (state) => {
    setRequested(state.resendOtp)
  },
  [receivedResendOtp.type]: (state) => {
    setReceived(state.resendOtp)
  },
  [failedResendOtp.type]: (state) => {
    setFailed(state.resendOtp)
  }
})

export default authReducer

// PUBLIC ACTIONS

// Thunk for assumedOut with side effects
export const assumedOutThunk = () => async (dispatch) => {
  localStorage.removeItem("assume_access_token")
  localStorage.removeItem("assume_refresh_token")
  const assumeRedirectUrl = localStorage.getItem("assume_redirect_url")
  localStorage.removeItem("assume_redirect_url")

  dispatch(assumedOut())

  await new Promise((resolve) => setTimeout(resolve, 100))

  if (assumeRedirectUrl) {
    window.location.replace(assumeRedirectUrl)
  } else {
    window.location.pathname = "/"
  }
}

export const signIn = (user) =>
  api({
    url: "/users/sign_in",
    method: "post",
    data: user,
    onStart: requested.type,
    onSuccess: loggedIn.type,
    onError: failed.type,
    props: { showToast: false }
  })

export const facebookSignIn = (data) =>
  api({
    url: "/users/auth/facebook/callback",
    method: "post",
    data,
    onStart: requested.type,
    onSuccess: loggedInByOAuth.type,
    onError: failed.type
  })

export const googleSignIn = (data) =>
  api({
    url: `/users/auth/google_oauth2/callback`,
    method: "post",
    data,
    onStart: requested.type,
    onSuccess: loggedInByOAuth.type,
    onError: failed.type
  })

export const appleSignIn = (data) =>
  api({
    url: `/users/auth/apple/callback`,
    method: "post",
    data,
    onStart: requested.type,
    onSuccess: loggedInByOAuth.type,
    onError: failed.type
  })

export const signOut = () => (dispatch) =>
  localStorage.getItem("assume_access_token")
    ? dispatch(assumedOutThunk())
    : dispatch(
        api({
          url: "/users/sign_out",
          method: "delete",
          onStart: requested.type,
          onSuccess: loggedOut.type,
          onError: failed.type
        })
      )

export const signUp = (user) =>
  api({
    url: "/users",
    method: "post",
    data: user,
    onStart: requested.type,
    onSuccess: signedUp.type, 
    onError: failed.type,
    props: { showToast: false }
  })

export const verifyByPinCode = (code) => (dispatch, getState) => {
  
  const { temp_token, loggedIn } = getState().auth || {}
  const config = {
    url: "/users/verify_by_pin_code",
    method: "post",
    data: { pin_code: code },
    onStart: requestedVerifyOTP.type,
    onSuccess: receivedVerifyOTP.type,
    onError: failedVerifyOTP.type
  }
  if (temp_token && !loggedIn) {
    config.noAuth = true
    config.data = { ...config.data, encrypted_uuid: temp_token }
  }
  return dispatch(api(config))
}

export const resendConfirmCode = (data) => (dispatch, getState) => {
  const { temp_token, loggedIn } = getState().auth || {}
  const config = {
    url: "/users/resend_confirmation_instructions",
    method: "put",
    data,
    onStart: requestedResendOtp.type,
    onSuccess: receivedResendOtp.type,
    onError: failedResendOtp.type
  }
  if (temp_token && !loggedIn) {
    config.noAuth = true
    config.data = { ...config.data, encrypted_uuid: temp_token }
  }
  return dispatch(api(config))
}

export const assume = (access_token, refresh_token, redirect_url) => assumedIn({ access_token, refresh_token, redirect_url })
